import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { Autocomplete, Box, IconButton, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Text } from '@app/ui/forms';
import { Tooltip } from '@app/ui/tooltip';

import { BOOLEAN_VALUES } from '../../constants';
import { ColorOperator, ConditionRowErrorTarget, RuleField } from '../../types';
import {
  ColorCaseFormValue,
  colorOperatorWireName,
  colorRowIssue,
  getColorValueControl,
  legalColorOperatorsFor,
} from '../../utils';

// One row shape covers BOTH a scoring rule's `cases[]` (fixed `field`, has `points`) and a gate's
// `conditions[]` (own `field` per row, no `points`) — PLAN P2-2 asks for exactly this reuse.
export interface ColorCaseRowProps {
  readonly name: string;
  readonly index: number;
  // Gate conditions pick their OWN field per row; a scoring case's field is fixed at the rule level.
  readonly showFieldPicker: boolean;
  readonly fields?: RuleField[];
  readonly fixedField?: RuleField;
  readonly showPoints: boolean;
  readonly disabled: boolean;
  readonly canRemove: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly onRemove: () => void;
  readonly onMove: (direction: -1 | 1) => void;
}

export const ColorCaseRow = ({
  name,
  index,
  showFieldPicker,
  fields,
  fixedField,
  showPoints,
  disabled,
  canRemove,
  canMoveUp,
  canMoveDown,
  onRemove,
  onMove,
}: ColorCaseRowProps) => {
  const { t } = useTranslation('common');
  const { values, setFieldValue, setFieldTouched, submitCount, touched } = useFormikContext<unknown>();

  const row = getIn(values, name) as ColorCaseFormValue | undefined;

  const field = showFieldPicker ? fields?.find(candidate => candidate.path === row?.field) : fixedField;
  const operators = useMemo(() => legalColorOperatorsFor(field), [field?.path, field?.operators]);
  const control = getColorValueControl(field?.type, row?.operator);
  const showError = submitCount > 0 || !!getIn(touched, name);
  const issue =
    showError && row
      ? colorRowIssue(row, field, key => t(key) ?? key, { requireField: showFieldPicker, requirePoints: showPoints })
      : undefined;
  const errorFor = (target: ConditionRowErrorTarget) => (issue?.target === target ? issue.message : undefined);
  const fieldError = errorFor(ConditionRowErrorTarget.field);
  const operatorError = errorFor(ConditionRowErrorTarget.operator);
  const valueError = errorFor(ConditionRowErrorTarget.value);
  const pointsError = errorFor(ConditionRowErrorTarget.points);
  const markTouched = (suffix: string) => setFieldTouched(`${name}.${suffix}`, true);

  // ⚠ The form state (`row.operator`) stays in the stored VALUE spelling throughout — matching
  // `ColorScoringRule.cases[]`/`ColorGate.conditions[]` and every helper in `utils/index.ts`
  // (`colorRowError`, `getColorValueControl`, `toColorCaseInput`, `toColorConditionInput`). The NAME
  // crossing (`colorOperatorWireName`) is used ONLY for the i18n lookup key here, and again once, at
  // the very end, when `toColorCaseInput`/`toColorConditionInput` build the mutation payload — never
  // stored back into the row (storing the NAME here would make that final crossing double-apply and
  // silently drop the operator for the 3/5 members whose name differs from its value).
  const operatorOptions = useMemo(
    () =>
      operators.map(operator => ({
        id: operator,
        name: t(`rules.colorCoding.operator.option.${colorOperatorWireName(operator)}`) ?? operator,
      })),
    [operators, t],
  );

  const optionChoices = useMemo(
    () => (field?.options ?? []).map(option => ({ id: option.key, name: option.label })),
    [field],
  );

  if (!row) {
    return null;
  }

  const handleFieldChange = (next: RuleField | null) =>
    setFieldValue(name, { ...row, field: next?.path ?? '', operator: '', value: '', values: [] });

  const handleOperatorChange = (next: string) =>
    setFieldValue(name, { ...row, operator: next as ColorOperator, value: '', values: [] });

  return (
    <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '16px', p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">{t('rules.colorCoding.row.heading', { number: index + 1 })}</Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('rules.editor.condition.moveUpButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canMoveUp} onClick={() => onMove(-1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('rules.editor.condition.moveDownButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canMoveDown} onClick={() => onMove(1)}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('rules.editor.condition.removeButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canRemove} onClick={onRemove}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {showFieldPicker && (
        <Autocomplete
          disabled={disabled}
          options={fields ?? []}
          value={field ?? null}
          groupBy={option => t(`rules.editor.appliesTo.${option.appliesTo}`) ?? option.appliesTo}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, selected) => option.path === selected.path}
          onChange={(_event, next) => handleFieldChange(next)}
          onBlur={() => markTouched('field')}
          renderInput={params => (
            <Text
              {...params}
              label={t('rules.editor.condition.fieldLabel')}
              error={!!fieldError}
              helperText={fieldError}
              sx={{ mb: 2 }}
            />
          )}
        />
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={theme => ({ width: { xs: '100%', sm: theme.spacing(22) } })}>
          <Text
            select
            disabled={disabled || !field}
            label={t('rules.editor.condition.operatorLabel')}
            error={!!operatorError}
            helperText={operatorError}
            value={row.operator}
            onChange={event => handleOperatorChange(event.target.value)}
            onBlur={() => markTouched('operator')}
            options={operatorOptions}
          />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 200 }} onBlur={() => markTouched('value')}>
          {control === 'none' && !!row.operator && (
            <Typography variant="body2" color="text.secondary">
              {t('rules.editor.condition.noValueNeeded')}
            </Typography>
          )}

          {control === 'boolean' && (
            <Text
              select
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
              options={BOOLEAN_VALUES.map(value => ({
                id: value,
                name: t(`general.option.${value === 'true' ? 'yes' : 'no'}`) ?? value,
              }))}
            />
          )}

          {control === 'optionSelect' && (
            <Text
              select
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
              options={optionChoices}
            />
          )}

          {control === 'optionMultiSelect' && (
            <Text
              select
              disabled={disabled}
              SelectProps={{ multiple: true }}
              label={t('rules.editor.condition.valuesLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.values}
              onChange={event => setFieldValue(`${name}.values`, event.target.value)}
              options={optionChoices}
            />
          )}

          {control === 'textList' && (
            <Autocomplete
              multiple
              freeSolo
              disabled={disabled}
              options={[] as string[]}
              value={row.values ?? []}
              onChange={(_event, next) => setFieldValue(`${name}.values`, next)}
              renderInput={params => (
                <Text
                  {...params}
                  label={t('rules.editor.condition.valuesLabel')}
                  error={!!valueError}
                  helperText={valueError ?? t('rules.colorCoding.row.valuesHelperText')}
                />
              )}
            />
          )}

          {control === 'number' && (
            <Text
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
            />
          )}

          {control === 'text' && (
            <Text
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
            />
          )}
        </Box>

        {showPoints && (
          <Box sx={theme => ({ width: { xs: '100%', sm: theme.spacing(22) } })}>
            <Text
              disabled={disabled}
              label={t('rules.colorCoding.row.pointsLabel')}
              error={!!pointsError}
              helperText={pointsError ?? t('rules.colorCoding.row.pointsHelperText')}
              value={row.points}
              onChange={event => setFieldValue(`${name}.points`, event.target.value)}
              onBlur={() => markTouched('points')}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
